import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, Building2, Tag as TagIcon, Trash2, Save, Plus, X,
  ExternalLink, User, MessageCircle, PhoneCall, StickyNote, RefreshCw,
} from "lucide-react";
import { ig } from "../inbox/_inbox_shared";
import { api } from "../../../lib/apiClient";

export default function ContactDetailPage() {
  const { contactId } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null); const [acts, setActs] = useState([]);
  const [err, setErr] = useState(null); const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false); const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState(""); const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [contact, activities] = await Promise.all([
        api.get(`/api/admin/contacts/${contactId}`),
        api.get(`/api/admin/contacts/${contactId}/activities?limit=50`),
      ]);
      setC(contact); setActs(activities.items || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setC((prev) => ({ ...prev, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const patch = {
        name: c.name, first_name: c.first_name, last_name: c.last_name,
        email: c.email, phone: c.phone, business: c.business,
        status: c.status, source: c.source,
      };
      await api.patch(`/api/admin/contacts/${contactId}`, patch);
      setDirty(false); await load();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const addTag = async () => {
    const t = newTag.trim(); if (!t) return;
    await api.post(`/api/admin/contacts/${contactId}/tags`, { add: [t], remove: [] });
    setNewTag(""); await load();
  };
  const removeTag = async (t) => {
    await api.post(`/api/admin/contacts/${contactId}/tags`, { add: [], remove: [t] });
    await load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/api/admin/contacts/${contactId}/note`, { note });
    setNote(""); await load();
  };
  const del = async () => {
    if (!window.confirm("Delete this contact permanently?")) return;
    await api.del(`/api/admin/contacts/${contactId}`);
    nav("/admin/contacts");
  };

  if (loading) return <div style={{ padding: 40, color: ig.muted }}>Loading…</div>;
  if (err)     return <div style={{ padding: 40, color: "#B91C1C" }}>Error: {err}</div>;
  if (!c)      return <div style={{ padding: 40 }}>Not found</div>;

  return (
    <div data-testid="contact-detail-page" style={{
      minHeight: "calc(100vh - 60px)", background: "#F6F8F7",
      fontFamily: "Inter, system-ui, sans-serif", color: ig.ink, padding: "20px 24px 60px",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <button data-testid="back-to-contacts" onClick={() => nav("/admin/contacts")} style={backBtn}>
          <ArrowLeft size={13} /> All Contacts
        </button>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }}>
          {c.name || "Unnamed contact"}
        </h1>
        <div style={{ flex: 1 }} />
        <button data-testid="cd-refresh" onClick={load} style={btnGhost}><RefreshCw size={13} /> Refresh</button>
        {dirty && <button data-testid="cd-save" onClick={save} style={btnPrimary} disabled={saving}>
          <Save size={13} /> {saving ? "Saving…" : "Save changes"}
        </button>}
        <button data-testid="cd-delete" onClick={del} style={{ ...btnGhost, color: "#B91C1C", borderColor: "#FECACA" }}>
          <Trash2 size={13} /> Delete
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div>
          {/* Details */}
          <Card title="Details">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Fld label="First name"><input style={input} value={c.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="cd-first" /></Fld>
              <Fld label="Last name"><input style={input}  value={c.last_name  || ""} onChange={(e) => set("last_name",  e.target.value)} data-testid="cd-last" /></Fld>
              <Fld label="Full name"><input style={input}  value={c.name       || ""} onChange={(e) => set("name",       e.target.value)} data-testid="cd-name" /></Fld>
              <Fld label="Business"><input style={input}   value={c.business   || ""} onChange={(e) => set("business",   e.target.value)} data-testid="cd-business" /></Fld>
              <Fld label="Email"><input style={input}      value={c.email      || ""} onChange={(e) => set("email",      e.target.value)} data-testid="cd-email" /></Fld>
              <Fld label="Phone"><input style={input}      value={c.phone      || ""} onChange={(e) => set("phone",      e.target.value)} data-testid="cd-phone" /></Fld>
              <Fld label="Status">
                <select style={input} value={c.status || "active"} onChange={(e) => set("status", e.target.value)} data-testid="cd-status">
                  {["active", "inactive", "unsubscribed", "bounced"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Fld>
              <Fld label="Source">
                <select style={input} value={c.source || "manual"} onChange={(e) => set("source", e.target.value)} data-testid="cd-source">
                  {["manual", "form", "import", "api", "chat", "call", "website", "facebook_ad", "google_ad"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Fld>
            </div>
          </Card>

          {/* Tags */}
          <Card title="Tags">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {(c.tags || []).length === 0 && <span style={{ color: ig.muted, fontSize: 12 }}>No tags yet.</span>}
              {(c.tags || []).map((t) => (
                <span key={t} data-testid={`tag-${t}`} style={tagPill}>
                  <TagIcon size={11} /> {t}
                  <button onClick={() => removeTag(t)} style={{ ...tagClose }} data-testid={`tag-remove-${t}`}><X size={9} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={newTag} onChange={(e) => setNewTag(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && addTag()}
                     placeholder="Add tag (Enter to add)" style={{ ...input, flex: 1 }} data-testid="cd-new-tag" />
              <button onClick={addTag} style={btnPrimary} data-testid="cd-add-tag"><Plus size={12} /> Add</button>
            </div>
          </Card>

          {/* Activity */}
          <Card title={`Activity (${acts.length})`}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Add a note (visible in activity history)…" style={{ ...input, flex: 1 }} data-testid="cd-note-input" />
              <button onClick={addNote} style={btnPrimary} data-testid="cd-note-add"><StickyNote size={12} /> Post note</button>
            </div>
            <div data-testid="cd-activity-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {acts.length === 0 && <div style={{ color: ig.muted, fontSize: 12 }}>No activity yet.</div>}
              {acts.map((a) => (
                <div key={a.id} style={{
                  display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                  border: `1px solid ${ig.borderSoft}`, borderRadius: 9, background: "#FAFCFB",
                }}>
                  <ActivityIcon type={a.type} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, color: ig.ink, fontWeight: 700 }}>{humanEvent(a.type)}</div>
                    {a.meta?.note && <div style={{ fontSize: 12, color: ig.inkSoft, marginTop: 3 }}>{a.meta.note}</div>}
                    {a.meta?.tags && <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 3 }}>Tags: {a.meta.tags.join(", ")}</div>}
                    {a.meta?.fields && <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 3 }}>Fields: {a.meta.fields.join(", ")}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: ig.muted, whiteSpace: "nowrap" }}>{fmt(a.ts)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right rail */}
        <div>
          <Card title="Ownership">
            <RailRow icon={Building2} label="Workspace">
              {c._workspace ? (
                <Link to={`/admin/workspaces/${c.workspace_id}/contacts`} style={linkStyle} data-testid="cd-open-workspace">
                  {c._workspace.name} <ExternalLink size={11} />
                </Link>
              ) : "—"}
            </RailRow>
            <RailRow icon={User} label="Owner">
              {c._owner ? (
                <Link to={`/admin/users/${c.owner_user_id}`} style={linkStyle} data-testid="cd-open-owner">
                  {c._owner.name || c._owner.email} <ExternalLink size={11} />
                </Link>
              ) : "—"}
            </RailRow>
            {c._agency && (
              <RailRow icon={Building2} label="Agency">
                <Link to={`/admin/agencies/${c.agency_id}/contacts`} style={linkStyle} data-testid="cd-open-agency">
                  {c._agency.name} · {c._agency.tier} <ExternalLink size={11} />
                </Link>
              </RailRow>
            )}
            <RailRow icon={Mail}  label="Email">{c.email || "—"}</RailRow>
            <RailRow icon={Phone} label="Phone">{c.phone || "—"}</RailRow>
          </Card>

          <Card title="Timeline">
            <RailRow label="Created">{fmt(c.created_at)}</RailRow>
            <RailRow label="Updated">{fmt(c.updated_at)}</RailRow>
            <RailRow label="Last activity">{fmt(c.last_activity_at)}</RailRow>
            <RailRow label="Last event type">{c.last_activity_type || "—"}</RailRow>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: ig.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function Fld({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: ig.ink, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
function RailRow({ icon: Icon, label, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: `1px dashed ${ig.borderSoft}`, fontSize: 12.5 }}>
      {Icon && <Icon size={12} color={ig.muted} />}
      <div style={{ color: ig.muted, minWidth: 90 }}>{label}</div>
      <div style={{ flex: 1, color: ig.ink, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{children}</div>
    </div>
  );
}
function ActivityIcon({ type }) {
  const map = { note_added: StickyNote, email_sent: Mail, email_opened: Mail, call_made: PhoneCall, sms_sent: MessageCircle, sms_received: MessageCircle, tag_added: TagIcon, tag_removed: TagIcon, created: Plus, updated: Save, reassigned: RefreshCw, status_changed: RefreshCw };
  const Icon = map[type] || StickyNote;
  return <span style={{ width: 28, height: 28, borderRadius: 999, background: "#EEF3F0", color: ig.ink, display: "inline-grid", placeItems: "center", flexShrink: 0 }}><Icon size={13} /></span>;
}
function humanEvent(t) {
  return { note_added: "Note added", email_sent: "Email sent", email_opened: "Email opened", call_made: "Call made", sms_sent: "SMS sent", sms_received: "SMS received", tag_added: "Tag added", tag_removed: "Tag removed", created: "Contact created", updated: "Contact updated", reassigned: "Reassigned", status_changed: "Status changed", merged: "Merged from duplicate" }[t] || t;
}
function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

const input    = { width: "100%", height: 36, padding: "0 11px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 13, outline: "none", color: ig.ink };
const backBtn  = { display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 12, color: ig.ink, cursor: "pointer", fontWeight: 700 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: 0, background: "linear-gradient(135deg, #18B86B, #22C55E)", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer" };
const btnGhost   = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontWeight: 600, fontSize: 12.5, cursor: "pointer" };
const tagPill  = { display: "inline-flex", alignItems: "center", gap: 4, background: "#EEF3F0", color: ig.inkSoft, fontSize: 11.5, fontWeight: 600, padding: "4px 4px 4px 8px", borderRadius: 999 };
const tagClose = { width: 16, height: 16, borderRadius: 999, border: 0, background: "transparent", color: ig.muted, cursor: "pointer", display: "inline-grid", placeItems: "center" };
const linkStyle = { color: "#2563EB", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700 };
